// Data Broker: Glass Annex (poi `broker`): a glass-walled intel booth with a bank of tiny camera-feed screens,
// an eye logo on a neon header, a cable run to the kerb and a data-grid pad on the ground. Art only.
import { hash2 } from '../../common/src/util';
import { type G, K, px, frame, shadow, disc, dk, lt, alpha, speckle } from './hd-kit';

/** cx, cy: the poi centre in scene hd px */
export function paintBroker(g: G, px0: number, py0: number) {
  // drawn 1.5x and centred on the counter: the booth is the kiosk (no separate kiosk icon)
  g.save(); g.translate(px0, py0 + 30); g.scale(1.5, 1.5); g.imageSmoothingEnabled = false;
  const cx = 0, cy = -30;
  // ground: dark glass data pad with a cyan grid and corner ticks
  const gx = cx - 30, gy = cy - 4, gw = 60, gh = 34;
  px(g, gx, gy, gw, gh, alpha(K.ink, 0.85));
  for (let i = 6; i < gw; i += 8) px(g, gx + i, gy, 1, gh, alpha(K.cyan, 0.18));
  for (let j = 6; j < gh; j += 8) px(g, gx, gy + j, gw, 1, alpha(K.cyan, 0.18));
  frame(g, gx, gy, gw, gh, alpha(K.cyan, 0.7), 2);
  for (const [x, y] of [[gx, gy], [gx + gw - 6, gy], [gx, gy + gh - 6], [gx + gw - 6, gy + gh - 6]]) px(g, x, y, 6, 6, K.cyan);

  // cable bundle running from the booth base off to the right
  for (let i = 0; i < 3; i++) {
    const c = [K.pink, K.cyan, K.amber][i];
    px(g, cx + 20, cy + 6 + i * 2, 38, 2, dk(c, 0.45));
    px(g, cx + 20, cy + 6 + i * 2, 38, 1, dk(c, 0.15));
  }
  px(g, cx + 56, cy + 3, 4, 10, K.metalLo); px(g, cx + 57, cy + 4, 2, 8, K.metal);

  // booth body: shadow, plinth, glass walls
  const bx = cx - 24, by = cy - 40, bw = 48, bh = 44;
  shadow(g, bx - 2, by + bh - 2, bw + 8, 8, 0.4);
  px(g, bx - 2, by + bh - 6, bw + 4, 8, K.metalLo); px(g, bx - 2, by + bh - 6, bw + 4, 2, K.metalHi); // plinth
  px(g, bx, by, bw, bh - 6, K.glassLo);
  px(g, bx, by, 3, bh - 6, K.metal); px(g, bx + bw - 3, by, 3, bh - 6, K.metal); // pillars
  px(g, bx + 1, by, 1, bh - 6, K.metalHi);

  // header: dark sign band with pink neon trim, eye logo and "intel" bars
  px(g, bx - 3, by - 10, bw + 6, 12, K.ink); frame(g, bx - 3, by - 10, bw + 6, 12, K.pink);
  px(g, bx - 3, by + 2, bw + 6, 2, dk(K.pink, 0.4));
  const ex = cx - 14, ey = by - 4;
  disc(g, ex, ey, 8, 4, K.white); disc(g, ex, ey, 4, 4, K.cyan); px(g, ex - 1, ey - 2, 3, 4, K.ink); px(g, ex - 3, ey - 3, 2, 1, K.white);
  for (let i = 0; i < 4; i++) px(g, cx + 2 + i * 6, ey - 3, 4, 6 - (i % 2) * 2, i % 2 ? K.pink : K.cyan);

  // bank of camera feeds: 4x3 tiny screens, each its own tint, some flickering, one marked red (a cop cam)
  const sx = bx + 5, sy = by + 8, cw = 8, ch = 6;
  for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) {
    const x = sx + c * (cw + 1), y = sy + r * (ch + 2), v = hash2(c * 7 + 1, r * 5 + 2);
    px(g, x - 1, y - 1, cw + 2, ch + 2, K.ink);
    const tint = v < 0.5 ? K.cyan : v < 0.8 ? K.glassHi : K.amber;
    px(g, x, y, cw, ch, dk(tint, 0.55));
    px(g, x, y + 1, cw, 1, alpha(tint, 0.6)); // scan line
    if (v > 0.72) px(g, x + 2, y + 2, 3, 3, K.red); // flagged camera
    else px(g, x + 1 + Math.floor(v * 4), y + 3, 2, 2, lt(tint, 0.3)); // a figure
    if (v < 0.2) px(g, x, y, cw, ch, alpha(K.night, 0.45)); // dead feed
  }
  // counter ledge and a scrolling data strip below the screens
  px(g, bx, by + 33, bw, 3, K.metalHi); px(g, bx, by + 36, bw, 2, K.metalLo);
  for (let i = 0; i < bw - 6; i += 3) px(g, bx + 3 + i, by + 34, 2, 1, hash2(i, 9) < 0.5 ? K.cyan : K.pink);

  // glass glare and a lit rim
  for (let i = 0; i < 6; i++) px(g, bx + 28 + i * 2, by + 2 + i * 5, 2, 6, alpha(K.white, 0.07));
  frame(g, bx, by, bw, bh - 6, alpha(K.cyan, 0.55));
  speckle(g, bx, by + bh - 6, bw, 6, K.metalHi, 0.1, 4);
  g.restore();
}
